"use client";

import { getCountryCodeFromLocale, getLangFromLocale, isStringTrue } from "@/apiUtils/commonUtils";
import { isInHomePage } from "@/utils/common";
import { cookieKeys } from "@/apiUtils/constants";
import { getCookieClient } from "@/apiUtils/cookieClientUtils";
import { createCookie } from "@/apiUtils/cookieUtils";
import CloseIcon from "@/assets/icons/close.svg?react";
import DoctorMLogo from "@/assets/logos/doctor_m_logo.svg";
import { LocaleSelectorOverlayData } from "@/common/lib/types";
import Modal from "@/components/ui/modal_new/Modal";
import Select from "@/components/ui/select/Select";
import { egyptIcon, ksaIcon, kuwaitIcon, qatarIcon, uaeIcon } from "@/constants/icons";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { languages, regions } from "@/i18n/routing";
import { cn } from "@/lib/tailwind";
import { useGlobalStore } from "@/store/store";
import { getCloudinaryURL } from "@/utils/cloudinary";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import NoImageSrc from "@/assets/images/no_image.svg";

interface IProps {
  data: LocaleSelectorOverlayData;
  locale: string;
  maykaPlpUtmSourceKeyword?: string;
}

const MAYKA_JUST_SUBMITTED_KEY = "mayka_locale_just_submitted";

const mapMap = {
  uae: uaeIcon,
  ksa: ksaIcon,
  qatar: qatarIcon,
  kuwait: kuwaitIcon,
  egypt: egyptIcon,
} as const;

const LocaleSelectorOverlay: React.FC<IProps> = ({
  data,
  locale,
  maykaPlpUtmSourceKeyword,
}: IProps) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<any>({});
  const isLargeScreen = useMediaQuery("(min-width: 768px)");

  const maykaKeyword = maykaPlpUtmSourceKeyword || "";

  const router = useRouter();
  const hasHandledOverlay = useRef(false);
  const overlayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const localeSelectorOverlayDelayRef = useRef<number>(data?.overlayLaunchDelay ?? 0);

  const isMaykaContext = useMemo(() => {
    const keyword = maykaKeyword.trim().toLowerCase();
    if (!keyword) return false;
    const utmSourceMayka = searchParams.get("utm_source")?.toLowerCase() === keyword;
    return utmSourceMayka;
  }, [searchParams, maykaKeyword]);

  const [lang, setLang] = useState<string>(
    useMemo(() => getLangFromLocale(locale as string), [locale]),
  );

  const [region, setRegion] = useState<string>(
    useMemo(() => getCountryCodeFromLocale(locale as string), [locale]),
  );

  const filteredRegions = useMemo(() => {
    return regions.filter(
      (loc) =>
        !(process?.env?.NEXT_PUBLIC_NON_NAVIGATIONAL_COUNTRIES?.split(",") || []).includes(
          loc?.code,
        ),
    );
  }, [regions]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleDoShowOverlay = useCallback((isMayka: boolean) => {
    setIsOpen(true);
    if (!isMayka) {
      createCookie(cookieKeys.HAS_SHOWN_HOME_OVERLAY, "true");
    }
  }, []);

  const shouldShowOverlay = useCallback(() => {
    if (isMaykaContext) {
      const authenticated = useGlobalStore.getState().isAuthenticated;
      if (authenticated) {
        return false;
      }
      try {
        if (globalThis.window !== undefined && sessionStorage.getItem(MAYKA_JUST_SUBMITTED_KEY)) {
          sessionStorage.removeItem(MAYKA_JUST_SUBMITTED_KEY);
          return false;
        }
      } catch {
        // ignore
      }
      return true;
    }
    const hasShownOverlay = isStringTrue(getCookieClient(cookieKeys?.HAS_SHOWN_HOME_OVERLAY));
    return !hasShownOverlay;
  }, [isMaykaContext]);

  const initializeOverlay = useCallback(() => {
    if (hasHandledOverlay.current) {
      return;
    }

    hasHandledOverlay.current = true;
    if (!shouldShowOverlay()) {
      return;
    }

    const delay = localeSelectorOverlayDelayRef.current;
    if (delay > 0) {
      overlayTimeoutRef.current = setTimeout(() => {
        handleDoShowOverlay(isMaykaContext);
      }, delay * 1000);
    } else {
      handleDoShowOverlay(isMaykaContext);
    }
  }, [shouldShowOverlay, handleDoShowOverlay, isMaykaContext, pathname]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setIsOpen(false);
      const newLocale = `${region}-${lang}`;
      if (isMaykaContext && pathname) {
        try {
          if (globalThis.window !== undefined) {
            sessionStorage.setItem(MAYKA_JUST_SUBMITTED_KEY, "1");
          }
        } catch {
          // ignore
        }
        const pathWithoutLocale = pathname.replace(/^\/[^/]+/, "") || "";
        const newPath = `/${newLocale}${pathWithoutLocale}`;
        const queryString = searchParams?.toString();
        router.replace(queryString ? `${newPath}?${queryString}` : newPath);
      } else {
        router.replace(`/${newLocale}`);
      }
    },
    [region, lang, router, isMaykaContext, pathname, searchParams],
  );

  const handleModalClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleRegionChange = useCallback(
    (selectedRegion: string) => {
      setRegion(selectedRegion);
    },
    [region],
  );

  const handleLanguageChange = useCallback(
    (selectedLang: string) => {
      setLang(selectedLang);
    },
    [lang],
  );

  useEffect(() => {
    const initTimer = setTimeout(() => {
      initializeOverlay();
    }, 500);

    return () => {
      clearTimeout(initTimer);
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        slots={{
          close: {
            visible: false,
          },
          container: {
            className: data?.isWithImage ? "p-0 md:w-[43.75rem] w-full" : "p-0 md:w-[30rem] w-full",
          },
        }}
      >
        <div className="flex flex-col justify-between md:flex-row" onClick={handleModalClick}>
          {data?.isWithImage && data?.media && (
            <section className="w-full flex-1 md:w-1/2">
              <Image
                src={
                  isLargeScreen
                    ? (getCloudinaryURL(data.media.largeMediaUrl) ?? NoImageSrc)
                    : (getCloudinaryURL(data.media.smallMediaUrl) ?? NoImageSrc)
                }
                alt="locale Image"
                height={500}
                width={400}
                className="h-full w-full object-cover"
              />
            </section>
          )}
          <section className="flex flex-1 flex-col items-center justify-between gap-1">
            {((!isLargeScreen && !data.media) || (isLargeScreen && data.media)) && (
              <div className="flex w-full flex-row items-center justify-end p-4">
                <button onClick={handleClose} type="button">
                  <CloseIcon className={cn("h-3 w-3")} />
                </button>
              </div>
            )}
            <div className="flex w-full flex-1 flex-col items-center justify-start gap-8 p-8">
              <Image src={DoctorMLogo} alt="DoctorM Logo" height={22} width={150} />
              <p className="max-w-[11.875rem] text-center text-[1.125rem] font-medium">
                {data.description}
              </p>

              <form className="flex w-full flex-col gap-12" onSubmit={handleSubmit}>
                <div className="flex w-full flex-col gap-[0.75rem]">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={region}
                      menuProps={{
                        className: "border-none bg-[#EEEEEE] mt-0 z-[99999]",
                      }}
                      triggerProps={{
                        className:
                          "w-full min-w-[9rem] border-none bg-[#EEEEEE] text-[0.75rem] font-medium text-[#999999]",
                      }}
                      parentProps={{ className: "flex-1" }}
                      disabled={false}
                      triggerLabel={lang === "ar" ? "اختر البلد" : "Select Country"}
                      onChange={(_, val) => handleRegionChange(val as string)}
                      options={filteredRegions?.map((item) => ({
                        label: (
                          <div className="flex items-center justify-start gap-2 text-[0.75rem] font-medium text-[#999999]">
                            <span className="border border-gray-200">
                              <Image
                                src={mapMap[item?.shortName?.toLowerCase() as keyof typeof mapMap]}
                                alt={item.shortName}
                                className="h-3 w-5"
                              />
                            </span>
                            <span>{item?.name}</span>
                          </div>
                        ),
                        value: item?.code,
                      }))}
                    />
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={lang}
                      menuProps={{
                        className: "border-none bg-[#EEEEEE] mt-0 z-[99999]",
                      }}
                      triggerProps={{
                        className:
                          "w-full min-w-[9rem] border-none bg-[#EEEEEE] text-[0.75rem] font-medium text-[#999999]",
                      }}
                      parentProps={{ className: "flex-1" }}
                      triggerLabel="Select Language"
                      onChange={(_, val) => handleLanguageChange(val as string)}
                      options={languages?.map((item) => ({ label: item.name, value: item.code }))}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="flex w-full items-center justify-center gap-2 bg-black px-4 py-2 text-center text-sm font-semibold text-white transition-colors hover:bg-gray-800"
                >
                  <p>{data.buttonTitle}</p>
                </button>
              </form>
            </div>
          </section>
        </div>
      </Modal>
    </>
  );
};

export default LocaleSelectorOverlay;
